/* Editor App shell — Feb-2026.
 *
 * A dedicated app that lives outside The Loom + Admin Console. Editors
 * see ONLY:
 *   • Login (uses admin_credentials with the `editor` scope)
 *   • Their assigned review queue
 *   • The review workspace (reader + rating + suggestions)
 *
 * There is no sidebar into /admin or /app. The bell + settings from
 * the existing admin surface stay because they read the same session
 * cookie; this component intentionally does NOT render them so the
 * editor's world is uncluttered.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Route, Routes, useNavigate, useLocation, useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, LogOut, BookOpen, ShieldCheck, Library, GitBranch, LibraryBig } from 'lucide-react';

import EditorDashboard from './EditorDashboard';
import EditorReviewWorkspace from './EditorReviewWorkspace';
import EditorPublicationWorkbench from './EditorPublicationWorkbench';
import EditorGenreTreePanel from './EditorGenreTreePanel';
import EditorAllManuscriptsPanel from './EditorAllManuscriptsPanel';

const API  = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });


export default function EditorApp() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Active tab: review workspace (/editor/manuscript/:rid) counts as Review queue
  const isActive = (path) => {
    if (path === '/editor') {
      return location.pathname === '/editor' || location.pathname === '/editor/' || /^\/editor\/manuscript\//.test(location.pathname);
    }
    return location.pathname.startsWith(path);
  };

  const navBtn = (path, label, icon) => (
    <button
      onClick={() => navigate(path)}
      className={`inline-flex items-center gap-1.5 rounded-sm border px-3 py-1.5 text-[11px] transition-all ${
        isActive(path)
          ? 'border-primary bg-primary/15 text-primary font-semibold shadow-[inset_0_-2px_0_0_hsl(var(--primary))]'
          : 'border-border hover:bg-secondary text-foreground/70 hover:text-foreground'
      }`}
      data-testid={`editor-nav-${path === '/editor' ? 'review-queue' : path.replace('/editor/', '')}`}
    >
      {icon}
      {label}
    </button>
  );

  const probe = useCallback(async () => {
    setLoading(true);
    try {
      const r = await http.get('/admin/me');
      const scopes = r.data?.scopes || [];
      if (!scopes.includes('editor') && !scopes.includes('pro_writer')) {
        setSession('denied');
      } else {
        setSession(r.data);
      }
    } catch {
      setSession(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { probe(); }, [probe]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading editor…
      </div>
    );
  }
  if (!session) return <EditorLoginGate onDone={probe} />;
  if (session === 'denied') {
    return (
      <div className="mx-auto mt-20 max-w-md rounded-md border border-border bg-card p-6 text-center">
        <ShieldCheck className="mx-auto mb-2 h-6 w-6 text-rose-600" />
        <div className="text-[14px] font-semibold">No editor access</div>
        <div className="mt-1 text-[12px] text-muted-foreground">
          Your account is signed in but lacks the <b>editor</b> scope. Ask
          a pro_writer admin to promote you from Admin → Editor Seats.
        </div>
        <button
          onClick={async () => { try { await http.post('/admin/logout'); } catch {} navigate('/editor', { replace: true }); window.location.reload(); }}
          className="mt-3 inline-flex items-center gap-1 rounded-md border border-border px-3 py-1 text-[12px] hover:bg-secondary">
          <LogOut className="h-3 w-3" /> Sign out
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-screen min-h-0 flex-col overflow-hidden bg-background" data-testid="editor-app">
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-border bg-panel-header/40 px-3 py-2.5 sm:px-5">
        <div className="inline-flex items-center gap-3 text-[13px] font-semibold">
          {/* Back to Writer Studio */}
          <button
            type="button"
            onClick={() => navigate('/app')}
            title="Back to Writer Studio (The Loom)"
            data-testid="editor-nav-back-to-studio"
            className="inline-flex items-center gap-1 rounded-sm border border-border px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          >
            <BookOpen className="h-3 w-3" />
            Writer Studio
          </button>
          <span className="text-muted-foreground/40">|</span>
          <BookOpen className="h-4 w-4 text-primary" />
          Crown &amp; Rose — Editorial
        </div>
        <div className="flex items-center gap-2 text-[11.5px] text-muted-foreground">
          <span>Signed in as <b className="text-foreground">{session.username}</b></span>
          <button
            onClick={async () => { try { await http.post('/admin/logout'); } catch {} window.location.href = '/editor'; }}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 hover:bg-secondary"
            data-testid="editor-logout-btn">
            <LogOut className="h-3 w-3" /> Sign out
          </button>
        </div>
      </header>
      <nav className="shrink-0 overflow-x-auto border-b border-border bg-card/60" data-testid="editor-primary-navigation">
        <div className="mx-auto flex w-max min-w-full max-w-[1400px] gap-1 px-3 py-2 sm:px-5">
          {navBtn('/editor',              'Review queue',         <BookOpen    className="h-3.5 w-3.5" />)}
          {navBtn('/editor/manuscripts',  'All manuscripts',      <LibraryBig  className="h-3.5 w-3.5" />)}
          {navBtn('/editor/publications', 'Publication Workbench',<Library     className="h-3.5 w-3.5" />)}
          {navBtn('/editor/genres',       'Genre Tree',           <GitBranch   className="h-3.5 w-3.5" />)}
        </div>
      </nav>
      <main className="min-h-0 w-full max-w-[1400px] flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 scroll-thin sm:mx-auto sm:px-5" data-testid="editor-page-scroll-area">
        <Routes>
          <Route path=""              element={<EditorDashboard />} />
          <Route path="manuscripts"   element={<EditorAllManuscriptsPanel />} />
          <Route path="manuscript/:rid" element={<EditorReviewWorkspaceRouted />} />
          <Route path="publications"  element={<EditorPublicationWorkbench />} />
          <Route path="genres"        element={<EditorGenreTreePanel />} />
          <Route path="*"             element={<Navigate to="" replace />} />
        </Routes>
      </main>
    </div>
  );
}


function EditorReviewWorkspaceRouted() {
  const { rid } = useParams();
  return <EditorReviewWorkspace reviewId={rid} />;
}


function EditorLoginGate({ onDone }) {
  const [user, setUser] = useState('');
  const [pw,   setPw]   = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post('/admin/login', { user, password: pw });
      toast.success('Signed in.');
      await onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sign-in failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <form onSubmit={submit}
            className="w-full max-w-sm rounded-lg border border-border bg-card p-6 shadow-sm"
            data-testid="editor-login-form">
        <div className="mb-3 text-center">
          <BookOpen className="mx-auto mb-1 h-6 w-6 text-primary" />
          <div className="text-[14px] font-semibold">Editor sign-in</div>
          <div className="mt-1 text-[11px] text-muted-foreground">
            Use your admin username + password. Editor scope required.
          </div>
        </div>
        <label className="mb-1 block text-[11px] uppercase tracking-wider">Username</label>
        <input value={user} onChange={(e) => setUser(e.target.value)}
               autoComplete="username" required
               data-testid="editor-login-user"
               className="mb-2 w-full rounded-md border border-border bg-background px-2 py-1.5 text-[12px]" />
        <label className="mb-1 block text-[11px] uppercase tracking-wider">Password</label>
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
               autoComplete="current-password" required
               data-testid="editor-login-password"
               className="mb-3 w-full rounded-md border border-border bg-background px-2 py-1.5 text-[12px]" />
        <button type="submit" disabled={busy}
                data-testid="editor-login-submit"
                className="inline-flex w-full items-center justify-center gap-1 rounded-md bg-primary py-1.5 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Sign in
        </button>
      </form>
    </div>
  );
}
